import React from 'react';
import { ClipboardList, CreditCard, Landmark, Check, Save, Loader2 } from 'lucide-react';
import i18n from '../../../../../i18n';

// A single "Mzadcom Account" / "Client Account" choice row.
const RoutingOption = ({ title, description, recommended, selected, onSelect }) => (
  <div
    className={`routing-option${selected ? ' routing-option--selected' : ''}`}
    role="radio"
    aria-checked={selected}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect()}
  >
    <span className="routing-option__radio">
      <span className="routing-option__radio-dot" />
    </span>
    <div className="routing-option__content">
      <div className="routing-option__title-row">
        <span className="routing-option__title">{title}</span>
        {recommended && <span className="routing-badge">{i18n.t('recommended')}</span>}
      </div>
      <p className="routing-option__description">{description}</p>
    </div>
  </div>
);

// The bank-account picker shown under "Client Account" once it's selected
// as the destination. Single-select — the backend stores one
// `organization_bank_account_id` per configuration.
const DestinationAccountPicker = ({ accounts, selectedId, onSelectAccount }) => (
  <div className="destination-panel">
    <p className="destination-panel__label">{i18n.t('select_destination_account')}</p>

    <div className="destination-panel__list">
      {accounts.map((account) => {
        const isSelected = selectedId === account.id;

        return (
          <div
            key={account.id}
            className={`destination-account-row${isSelected ? ' destination-account-row--selected' : ''}`}
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            onClick={() => onSelectAccount(account.id)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelectAccount(account.id)}
          >
            <span className="destination-account-row__icon">
              <Landmark size={16} />
            </span>

            <div className="destination-account-row__info">
              <p className="destination-account-row__name">
                {account.bank_name}
                {account.branch_name ? ` · ${account.branch_name}` : ''}
              </p>
              <p className="destination-account-row__number">
                {account.account_number ? `****${String(account.account_number).slice(-4)}` : '--'}
              </p>
            </div>

            {account.is_default && (
              <span className="destination-account-row__badge">{i18n.t('primary')}</span>
            )}

            <span className="destination-account-row__check">
              <Check size={12} strokeWidth={3} />
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const PaymentRoutingSettings = ({
  accounts,
  enrollmentSource,
  enrollmentAccountId,
  paymentDestination,
  paymentAccountId,
  isDirty,
  isSaving,
  onEnrollmentSourceChange,
  onEnrollmentAccountChange,
  onPaymentDestinationChange,
  onPaymentAccountChange,
  onSave,
  onDiscard,
}) => (
  <React.Fragment>
    <div className="routing-grid">
      {/* ---------------------------------------------------------------
       * Auction Enrollment
       * ------------------------------------------------------------- */}
      <div className="routing-card">
        <div className="routing-card__header">
          <span className="routing-card__icon">
            <ClipboardList size={20} />
          </span>
          <div>
            <p className="routing-card__title">{i18n.t('auction_enrollment')}</p>
          </div>
        </div>
        <p className="routing-card__subtitle">{i18n.t('auction_enrollment_subtitle')}</p>

        <div className="routing-card__options" role="radiogroup">
          <RoutingOption
            title={i18n.t('mzadcom_account')}
            description={i18n.t('mzadcom_account_description')}
            recommended
            selected={enrollmentSource === 'mzadcom'}
            onSelect={() => onEnrollmentSourceChange('mzadcom')}
          />
          <RoutingOption
            title={i18n.t('client_account')}
            description={i18n.t('client_account_enrollment_description')}
            selected={enrollmentSource === 'organization'}
            onSelect={() => onEnrollmentSourceChange('organization')}
          />
        </div>

        {enrollmentSource === 'organization' && accounts.length > 0 && (
          <DestinationAccountPicker
            accounts={accounts}
            selectedId={enrollmentAccountId}
            onSelectAccount={onEnrollmentAccountChange}
          />
        )}
      </div>

      {/* ---------------------------------------------------------------
       * Auction Payment
       * ------------------------------------------------------------- */}
      <div className="routing-card">
        <div className="routing-card__header">
          <span className="routing-card__icon">
            <CreditCard size={20} />
          </span>
          <div>
            <p className="routing-card__title">{i18n.t('auction_payment')}</p>
          </div>
        </div>
        <p className="routing-card__subtitle">{i18n.t('auction_payment_subtitle')}</p>

        <div className="routing-card__options" role="radiogroup">
          <RoutingOption
            title={i18n.t('mzadcom_account')}
            description={i18n.t('mzadcom_account_auction_payment_description')}
            recommended
            selected={paymentDestination === 'mzadcom'}
            onSelect={() => onPaymentDestinationChange('mzadcom')}
          />
          <RoutingOption
            title={i18n.t('client_account')}
            description={i18n.t('client_account_auction_payment_description')}
            selected={paymentDestination === 'organization'}
            onSelect={() => onPaymentDestinationChange('organization')}
          />
        </div>

        {paymentDestination === 'organization' && accounts.length > 0 && (
          <DestinationAccountPicker
            accounts={accounts}
            selectedId={paymentAccountId}
            onSelectAccount={onPaymentAccountChange}
          />
        )}
      </div>
    </div>

    {/* -----------------------------------------------------------------
     * Save bar — only meaningful once something has actually changed
     * ------------------------------------------------------------- */}
    <div className="routing-save-bar">
      <p className="routing-save-bar__hint">
        {isDirty ? i18n.t('unsaved_routing_changes') : i18n.t('routing_up_to_date')}
      </p>

      <div className="routing-save-bar__actions">
        {isDirty && !isSaving && (
          <button type="button" className="routing-save-bar__discard" onClick={onDiscard}>
            {i18n.t('discard')}
          </button>
        )}

        <button
          type="button"
          className="routing-save-bar__save"
          disabled={!isDirty || isSaving}
          onClick={onSave}
        >
          {isSaving ? (
            <Loader2 size={16} className="routing-save-bar__spinner" />
          ) : (
            <Save size={16} />
          )}
          {isSaving ? i18n.t('saving') : i18n.t('save_changes')}
        </button>
      </div>
    </div>
  </React.Fragment>
);

export default PaymentRoutingSettings;
